// This is the parent component of a modal: ModalReferralstwo

import * as PRTNR from "./prtnr";

import styled from "@emotion/styled";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ActivityList from "./ActivityList";
import { InfoCircleIcon, UrgentIcon } from "./assets";
import GenericModal from "./GenericModal";
import ModalReferralstwo from "./ModalReferralstwo";
import ModalSaveSuccess from "./ModalSaveSuccess";
import { fetchReferralHistoryItems } from "./redux/slices/ReferralHistoryThunk";
import { resetUpdateSuccess } from "./redux/slices/ReferralsSlice";
import {
  fetchReferralByID,
  updateReferral,
} from "./redux/slices/ReferralsThunks";
import { formatDate, formatDate2, formatTime } from "./utils";
import ToolTip from "./prtnr/ui/ToolTip";

const statusColors = {
  Won: "rgba(18, 124, 78, 1)",
  "Verbally committed": "rgba(244, 186, 52, 1)",
  "Proposal sent": "rgba(244, 186, 52, 1)",
  Lost: "rgba(240, 70, 63, 1)",
  "Lead identified": "rgba(89, 84, 222, 1)",
  "Opportunity identified": "rgba(89, 84, 222, 1)",
  Opportunity: "rgba(89, 84, 222, 1)",
  All: "rgba(100, 116, 139, 1)",
};

// Constants
const STATUS_ORDER = [
  "lead identified",
  "opportunity",
  "proposal sent",
  "verbally committed",
  "won",
  "lost",
];

const FILTERED_ACTIONS = ["Tracking", "Successful", "Unsuccessful", "Paid"];
const getStatusColor = (action = "") => {
  const entry = Object.entries(statusColors).find(([status]) =>
    action.toLowerCase().includes(status.toLowerCase())
  );
  return entry ? entry[1] : statusColors["All"];
};

export default function ReferralsDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();

  // Redux state
  const historyItems = useSelector((state) => state.referralHistory.items);
  const referral = useSelector((state) => state.referrals.referralDetails);
  const isLoading = useSelector((state) => state.referrals.loading);
  const error = useSelector((state) => state.referrals.error);

  // Local state
  const [notes, setNotes] = useState("");
  const [closeDate, setCloseDate] = useState("");
  const [expectedDateError, setExpectedDateError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openModalUpdateSuccess, setOpenModalUpdateSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const updateSuccess = useSelector((state) => state.referrals.updateSuccess);

  // Fetch referral data on mount or ID change
  useEffect(() => {
    if (id) {
      dispatch(fetchReferralByID(id));
      dispatch(fetchReferralHistoryItems(id));
    }
  }, [dispatch, id]);

  // Sync local state with referral data when it loads
  useEffect(() => {
    if (referral) {
      setNotes(referral.notes || "");
      setCloseDate(referral.expected_close_date || "");
    }
  }, [referral?._id]);

  const hasChanges = useMemo(() => {
    if (!referral) return false;
    const originalNotes = referral.notes || "";
    const originalCloseDate = referral.expected_close_date || "";
    return originalNotes !== notes || originalCloseDate !== closeDate;
  }, [referral, notes, closeDate]);

  // Validation
  const validateCloseDate = useCallback(() => {
    if (!referral) return true;

    const currentStatus = referral.referral_status?.toLowerCase();
    const currentIndex = STATUS_ORDER.indexOf(currentStatus);
    const requiredIndex = STATUS_ORDER.indexOf("opportunity");

    if (currentIndex >= requiredIndex && !closeDate) {
      setExpectedDateError("Expected Close Date is required for this status.");
      return false;
    }

    setExpectedDateError("");
    return true;
  }, [referral, closeDate]);

  // Handlers
  const handleDateChange = useCallback((e) => {
    setCloseDate(e.target.value);
    setExpectedDateError(""); // Clear error on change
  }, []);

  const handleUpdate = useCallback(async () => {
    if (!referral || !hasChanges) return;

    if (!validateCloseDate()) return;

    setIsSaving(true);

    try {
      const data = {
        _id: referral._id,
        notes,
        expected_close_date: closeDate,
        updated_at: Date.now(),
      };

      await dispatch(updateReferral(data));
      await dispatch(fetchReferralHistoryItems(referral._id));
      setOpenModalUpdateSuccess(true);
    } catch (error) {
      console.error("Failed to update referral:", error);
    } finally {
      setIsSaving(false);
    }
  }, [referral, hasChanges, validateCloseDate, notes, closeDate, dispatch]);

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (updateSuccess && id) {
      dispatch(fetchReferralHistoryItems(id));
      dispatch(fetchReferralByID(id));
      dispatch(resetUpdateSuccess());
    }
  }, [updateSuccess, id, dispatch]);

  const handleCloseSuccessModal = useCallback(() => {
    setOpenModalUpdateSuccess(false);
  }, []);

  // Loading state
  if (isLoading && !referral) {
    return (
      <RootWrapperReferralstwo>
        <Dashboard>
          <Wrapper>
            <div>Loading...</div>
          </Wrapper>
        </Dashboard>
      </RootWrapperReferralstwo>
    );
  }

  // Error state
  if (error && !referral) {
    return (
      <RootWrapperReferralstwo>
        <Dashboard>
          <Wrapper>
            <div>Error loading referral: {error}</div>
          </Wrapper>
        </Dashboard>
      </RootWrapperReferralstwo>
    );
  }

  // No referral found
  if (!referral) {
    return (
      <RootWrapperReferralstwo>
        <Dashboard>
          <Wrapper>
            <div>Referral not found</div>
          </Wrapper>
        </Dashboard>
      </RootWrapperReferralstwo>
    );
  }
  return (
    <RootWrapperReferralstwo>
      <Dashboard>
        <Wrapper>
          <DashboardHeader>
            <Frame1462>
              <LinkButton
                onClick={() => {
                  navigate("/referrals");
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.21875 7.5625C0.0625 7.40625 0 7.21875 0 7C0 6.8125 0.0625 6.625 0.21875 6.46875L5.71875 1.21875C6.03125 0.9375 6.5 0.9375 6.78125 1.25C7.0625 1.53125 7.0625 2.03125 6.75 2.3125L2.59375 6.25H13.25C13.6562 6.25 14 6.59375 14 7C14 7.4375 13.6562 7.75 13.25 7.75H2.59375L6.75 11.7188C7.0625 12 7.0625 12.4688 6.78125 12.7812C6.5 13.0938 6.03125 13.0938 5.71875 12.8125L0.21875 7.5625Z"
                    fill="#64748B"
                  />
                </svg>
                <GetStarted>Back</GetStarted>
              </LinkButton>
              <WelcomeJames>Referral Details</WelcomeJames>
            </Frame1462>
            <Frame1461 />
          </DashboardHeader>
          <PRTNR.ReferralStatusCard
            status={referral.referral_status}
            updatedAt={formatDate(referral.updated_at)}
            note={referral.notes}
            onChangeStatus={handleOpenModal}
            showCustomWon={true}
            LostReason={referral.referral_lost_reason}
          />
          <Frame1485>
            <Frame1468_0001>
              <Frame1484>
                <ReferralsDetailsText>Referral Details</ReferralsDetailsText>
                <Frame1539>
                  <SubmittedOn>Submitted on</SubmittedOn>
                  <Frame1551>
                    <_1Apr2025>{formatDate(referral?.created_at)}</_1Apr2025>
                    <_1010Am>{formatTime(referral?.created_at)}</_1010Am>
                  </Frame1551>
                </Frame1539>
              </Frame1484>
              <Frame1485_0001>
                <Frame1538>
                  <ContactPerson>Contact Person</ContactPerson>
                  <JohnnyDepp>
                    {referral?.member_id?.first_name}{" "}
                    {referral?.member_id?.last_name}
                  </JohnnyDepp>
                </Frame1538>
                <Frame1539_0001>
                  <ContactNumber>Contact Number</ContactNumber>
                  <_4412345677890>
                    {referral?.member_id?.phone_number}
                  </_4412345677890>
                </Frame1539_0001>
                <Frame1538>
                  <EmailAddress>Email address</EmailAddress>
                  <JhonnydeppEmailCom>
                    {referral?.member_id?.email_address}
                  </JhonnydeppEmailCom>
                </Frame1538>
              </Frame1485_0001>
              <Frame1476>
                <CompanyWebsite>Potential Clients Website</CompanyWebsite>
                <WwwGreenleafcafeCo>
                  {referral?.company_website_url}
                </WwwGreenleafcafeCo>
              </Frame1476>
              <Frame1473>
                <Frame1470_0001>
                  <Frame1476>
                    <Sector>Sector</Sector>
                    <TechnologySoftwareSaaS>
                      {referral?.industry_sector}
                    </TechnologySoftwareSaaS>
                  </Frame1476>
                </Frame1470_0001>
                <Frame1471>
                  <Frame1476>
                    <Location>Primary Location</Location>
                    <LondonUk> {referral?.location}</LondonUk>
                  </Frame1476>
                </Frame1471>
              </Frame1473>
              <Frame1476>
                <DoesYourFriendHaveABudgetToWorkTo>
                  Does your friend have a budget to work to ?
                </DoesYourFriendHaveABudgetToWorkTo>
                <Unknown> {referral?.has_budget}</Unknown>
              </Frame1476>
              <Frame1473>
                <Frame1470_0001>
                  <Frame1476>
                    <IsYouFriendADecisionMaker>
                      Is you friend a decision maker
                    </IsYouFriendADecisionMaker>
                    <Yes>
                      {referral?.is_decision_maker === true
                        ? "Yes"
                        : referral?.is_decision_maker === false
                        ? "No"
                        : null}
                    </Yes>
                  </Frame1476>
                </Frame1470_0001>
                <Frame1471>
                  {referral?.is_urgent && (
                    <Frame1476>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <IsYouFriendADecisionMaker>
                          Priority
                        </IsYouFriendADecisionMaker>
                        <ToolTip
                          message="Urgent: time-sensitive referral. Quick response requested."
                          icon={UrgentIcon}
                        />
                      </div>
                      <Yes>{referral?.is_urgent ? "Urgent" : "No"}</Yes>
                    </Frame1476>
                  )}
                </Frame1471>
              </Frame1473>

              <Frame1476>
                <DoesYourFriendHaveAnUrgentProblemOrNeedToAddress>
                  Does your friend have an urgent problem or need to address?
                </DoesYourFriendHaveAnUrgentProblemOrNeedToAddress>
                <SelectedOption>
                  {" "}
                  {referral?.urgent_problem === true
                    ? "Yes"
                    : referral?.urgent_problem === false
                    ? "No"
                    : null}
                </SelectedOption>
              </Frame1476>
              <Frame1476>
                <InWhatTimeframeWillTheyNeedASolution>
                  In what timeframe will they need a solution?
                </InWhatTimeframeWillTheyNeedASolution>
                <SelectedOption>{referral?.timeframe}</SelectedOption>
              </Frame1476>
              <Frame1483>
                <Frame1483_0001>
                  <PleaseShareAnyRelevantContextOfYourReferral>
                    Please share any relevant context of your referral
                  </PleaseShareAnyRelevantContextOfYourReferral>
                </Frame1483_0001>
                <Frame1472>
                  <AddSomeNotesHere>
                    {referral?.relevant_context_of_referral}
                  </AddSomeNotesHere>
                </Frame1472>
              </Frame1483>
            </Frame1468_0001>
            <Right>
              <InternalNotes>
                <PRTNR.Input
                  label="Expected Close Date"
                  type="date"
                  min={new Date().toISOString().split("T")[0]}
                  value={formatDate2(closeDate)}
                  onChange={(e) => {
                    handleDateChange(e);
                  }}
                  error={expectedDateError}
                  errorMessage={expectedDateError}
                />
                <Frame1433>
                  <Frame1433_0001>
                    <InternalNotes_0001>Internal Notes</InternalNotes_0001>
                    <TooltipWrapper>
                      <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                      <TooltipText className="tooltip">
                        Use this field to note internal updates, actions, or
                        context on your referral’s progress. Visible only to
                        your team
                      </TooltipText>
                    </TooltipWrapper>
                  </Frame1433_0001>
                </Frame1433>
                <PRTNR.TextArea
                  value={notes}
                  onChange={(e) => {
                    setNotes(e.target.value);
                  }}
                />
                <ButtonContainer>
                  <PRTNR.Button
                    onClick={() => {
                      handleUpdate();
                    }}
                  >
                    Save Changes
                  </PRTNR.Button>
                </ButtonContainer>
              </InternalNotes>
            </Right>
          </Frame1485>
          <Activity>
            <Frame1433_0002>
              <ReferralStatus>Activity</ReferralStatus>
            </Frame1433_0002>
            {[...historyItems]
              ?.filter(
                (item) =>
                  !["Tracking", "Successful", "Unsuccessful", "Paid"].includes(
                    item.action
                  )
              )
              .reverse()
              .map((item, index) => (
                <ActivityList item={item} />
              ))}
          </Activity>
        </Wrapper>
      </Dashboard>

      <GenericModal isOpen={isModalOpen} onClose={handleCloseModal}>
        <ModalReferralstwo
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          referral={referral}
          setNotes={setNotes}
        />
      </GenericModal>

      <GenericModal
        isOpen={openModalUpdateSuccess}
        onClose={handleCloseSuccessModal}
      >
        <ModalSaveSuccess
          isOpen={openModalUpdateSuccess}
          onClose={handleCloseSuccessModal}
        />
      </GenericModal>
    </RootWrapperReferralstwo>
  );
}

const RootWrapperReferralstwo = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
`;

const Dashboard = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  background: rgb(241, 245, 249);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding-inline: clamp(1rem, 5vw, 3.75rem);
  flex-shrink: 0;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  flex: none;
  gap: 24px;
  max-width: 1200px;
  width: 100%;
  box-sizing: border-box;
  padding: 0px 0px 32px;
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  flex: none;
  gap: 8px;
  max-width: 1200px;
  width: 100%;
  box-sizing: border-box;
  padding: 16px 0px;
  flex-wrap: wrap;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const Frame1462 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 4px;
  width: 767px;
  box-sizing: border-box;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const LinkButton = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  flex: none;
  gap: 8px;
  box-sizing: border-box;
  padding: 8px 0px;
  cursor: pointer;
`;
const GetStarted = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  line-height: 140%;
  text-align: left;
`;

const WelcomeJames = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: clamp(1.5rem, 5vw, 3rem);
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  letter-spacing: -1px;
  text-align: center;
`;

const Frame1461 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  flex: none;
  gap: 8px;
  box-sizing: border-box;
`;

const Frame1485 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  flex: none;
  gap: 24px;
  max-width: 1200px;
  width: 100%;
  box-sizing: border-box;
  @media (max-width: 1024px) and (min-width: 768px) {
    flex-direction: column;
  }
  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const Frame1468_0001 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  gap: 16px;
  box-shadow: 0px 1px 2px rgba(10, 13, 20, 0.03);
  border: solid 1px rgb(225, 228, 234);
  border-radius: 16px;
  width: 70%;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 24px;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const Frame1484 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
  @media (max-width: 1024px) and (min-width: 768px) {
    flex-direction: row;
  }

  @media (max-width: 767px) {
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
  }
`;

const ReferralsDetailsText = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 18px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  width: 389px;
  min-height: 29px;
  flex: 1;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const Frame1539 = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-direction: row;
  align-items: center;
  flex: 1;
  gap: 8px;
  width: 387px;
  box-sizing: border-box;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const SubmittedOn = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
`;

const Frame1551 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  flex: none;
  gap: 8px;
  box-sizing: border-box;
`;

const _1Apr2025 = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
`;

const _1010Am = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
`;

const Frame1485_0001 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  box-shadow: 0px 1px 2px rgba(10, 13, 20, 0.03);
  border: solid 1px rgb(225, 228, 234);
  border-radius: 16px;
  align-self: stretch;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 8px 16px;
  flex-shrink: 0;
  @media (max-width: 1024px) and (min-width: 768px) {
    flex-direction: column;
  }

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const Frame1538 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  flex: 1;
  width: 239px;
  box-sizing: border-box;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 239px;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const ContactPerson = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  min-height: 29px;
  align-self: stretch;
  flex-shrink: 0;
`;

const JohnnyDepp = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;

const Frame1539_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  flex: 1;
  width: 239px;
  box-sizing: border-box;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 239px;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const ContactNumber = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  min-height: 29px;
  align-self: stretch;
  flex-shrink: 0;
`;

const _4412345677890 = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;

const EmailAddress = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  min-height: 29px;
  align-self: stretch;
  flex-shrink: 0;
`;

const JhonnydeppEmailCom = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
  /* Allow wrapping */
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;

  /* Layout constraints */
  max-width: 100%;
  display: inline-block;
`;

const Frame1476 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const CompanyWebsite = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;

const WwwGreenleafcafeCo = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;

const Frame1473 = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  align-items: flex-start;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame1470_0001 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 24px;
  width: 391px;
  box-sizing: border-box;
`;

const Sector = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;

const TechnologySoftwareSaaS = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;

const Frame1471 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 24px;
  width: 391px;
  box-sizing: border-box;
`;

const Location = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;

const LondonUk = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;

const DoesYourFriendHaveABudgetToWorkTo = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;

const Unknown = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;

const IsYouFriendADecisionMaker = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;

const Yes = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;

const DoesYourFriendHaveAnUrgentProblemOrNeedToAddress = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;

const SelectedOption = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;

const InWhatTimeframeWillTheyNeedASolution = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;

const Frame1483 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame1483_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const PleaseShareAnyRelevantContextOfYourReferral = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  width: 781px;
  flex: 1;
`;

const Frame1472 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  border-radius: 8px;
  align-self: stretch;
  min-height: 186px;
  background: rgb(243, 244, 251);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 16px;
  flex-shrink: 0;
`;

const AddSomeNotesHere = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
`;

const Right = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 8px;
  width: 347px;
  box-sizing: border-box;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const InternalNotes = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  box-shadow: 0px 1px 2px rgba(10, 13, 20, 0.03);
  border: solid 1px rgb(225, 228, 234);
  border-radius: 16px;
  align-self: stretch;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 24px;
  flex-shrink: 0;
`;

const Frame1433 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  align-self: stretch;
  flex-shrink: 0;
`;

const Frame1433_0001 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  gap: 2px;
  align-self: stretch;
  box-sizing: border-box;
`;

const InternalNotes_0001 = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 18px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  min-height: 29px;
  flex: 1;
`;

const PrtnrTextarea = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  height: 186px;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const GFieldsStacked_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 4px;
  align-self: stretch;
  height: 186px;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Field_0001 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  flex: 1;
  gap: 8px;
  border: solid 1px rgb(224, 228, 244);
  border-radius: 8px;
  align-self: stretch;
  height: 186px;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 8px 16px;
  flex-shrink: 0;
`;

const Placeholder_0001 = styled.span`
  color: rgb(179, 182, 195);
  text-overflow: ellipsis;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  width: 268px;
  flex: 1;
`;

const Activity = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  box-shadow: 0px 1px 2px rgba(10, 13, 20, 0.03);
  border: solid 1px rgb(225, 228, 234);
  border-radius: 16px;
  align-self: stretch;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 24px;
  flex-shrink: 0;
`;

const Frame1433_0002 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const ReferralStatus = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 18px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  width: 1152px;
  min-height: 29px;
  flex: 1;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: flex-end;
`;

const GFeildsInfo = styled.img`
  width: 18px;
  height: 18px;
  object-fit: cover;
`;

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;

  &:hover .tooltip {
    visibility: visible;
    opacity: 1;
  }
`;

const TooltipText = styled.div`
  visibility: hidden;
  color: #000000;
  font-weight: 500;
  font-size: 12px;
  text-align: start;
  padding: 8px 16px;
  border-radius: 4px;
  position: absolute;
  z-index: 1;
  top: 125%; /* Show below the icon */
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.3s;
  max-width: 280px;
  width: 280px;
  backdrop-filter: blur(20px);
  background: var(--backgrounds-primary, #ffffff);
  font-family: "Montserrat", sans-serif;
  &::after {
    content: "";
    position: absolute;
    bottom: 100%; /* Top of the tooltip box */
    left: 50%;
    margin-left: -6px;
    border-width: 6px;
    border-style: solid;
    border-color: transparent transparent var(--backgrounds-primary, #ffffff)
      transparent;
  }
`;
